import { Colors } from "@/enums/Colors";
import { CalendarToday, CloseOutlined, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, IconButton, Paper, SxProps, styled } from "@mui/material";
import { StaticDatePicker } from "@mui/x-date-pickers";
import React, { RefObject, useState } from "react";
import SearchField from "../utils/SearchField";

interface ConversationFilterProps {
  handleSearchByKeyword: (searchRef: RefObject<HTMLInputElement> | undefined) => void;
  handleSearchByDate: (date: Date | null) => void;
  handleClose: () => void;
  matchingMessageExternalId: string[] | null;
  scrollToMessageExternalId: string | null;
  goToNextMessage: () => void;
  goToPreviousMessage: () => void;
}

const ConversationFilter: React.FC<ConversationFilterProps> = ({
  handleSearchByKeyword,
  handleSearchByDate,
  handleClose,
  matchingMessageExternalId,
  scrollToMessageExternalId,
  goToNextMessage,
  goToPreviousMessage,
}) => {
  interface ArrowProps {
    sx?: SxProps;
  }
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const handleCalendarClick = () => {
    setCalendarOpen(!calendarOpen);
  };

  const handleCloseDatePicker = () => {
    setCalendarOpen(false);
  };

  const ArrowDown: React.FC<ArrowProps> = ({ sx }) => {
    const isDisabled =
      !matchingMessageExternalId || !scrollToMessageExternalId || matchingMessageExternalId.indexOf(scrollToMessageExternalId) == 0;

    return (
      <KeyboardArrowDown
        onClick={() => {
          if (!isDisabled) goToPreviousMessage();
        }}
        sx={{
          display: "block",
          transform: "scale(1.7)",
          zIndex: 1000,
          color: isDisabled ? Colors.LIGHT_GRAY_CDCDCD : "initial",
          cursor: isDisabled ? "default" : "pointer",
          ...sx,
        }}
      />
    );
  };

  const ArrowUp: React.FC<ArrowProps> = ({ sx }) => {
    const isDisabled =
      !matchingMessageExternalId ||
      !scrollToMessageExternalId ||
      matchingMessageExternalId.indexOf(scrollToMessageExternalId) == matchingMessageExternalId.length - 1;

    return (
      <KeyboardArrowUp
        onClick={() => {
          if (!isDisabled) goToNextMessage();
        }}
        sx={{
          transform: "scale(1.7)",
          display: "block",
          zIndex: 1000,
          color: isDisabled ? Colors.LIGHT_GRAY_CDCDCD : "initial",
          cursor: isDisabled ? "default" : "pointer",
          ...sx,
        }}
      />
    );
  };

  const CallendarPopOver = styled(Paper)(({ theme }) => ({
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1000,
    margin: "0.5rem 1rem 0rem 5rem",
    border: "0px solid #ccc",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    background: "white",
    padding: "10px",

    // hide calendar-header
    "& .MuiPickersToolbar-root": {
      display: "none",
    },
    "& .MuiDialogActions-root": {
      display: "none",
    },
  }));

  const handleOnDateClick = (date: Date | null) => {
    handleSearchByDate(date);
    setSelectedDate(date || new Date());
    handleCloseDatePicker();
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        alignItems: "center",
        gap: "0.9rem",
        width: "100%",
      }}
    >
      <ArrowUp sx={{ flexShrink: 0 }} />
      <ArrowDown sx={{ flexShrink: 0 }} />
      <SearchField
        triggerSearchOnInputChange={true}
        handleSearch={handleSearchByKeyword}
        displaySearchButton={false}
        fullWidth
        containerProps={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
        }}
      />
      <IconButton onClick={handleCalendarClick}>
        <CalendarToday />
      </IconButton>

      {calendarOpen && (
        <CallendarPopOver>
          <StaticDatePicker value={selectedDate} onChange={handleOnDateClick} />
        </CallendarPopOver>
      )}
      <CloseOutlined onClick={handleClose} sx={{ transform: "scale(1.3)", flexShrink: 0, cursor: "pointer" }} />
    </Box>
  );
};

export default ConversationFilter;
